import { DoubleFestivalBanner } from '@/app/components/shared/DoubleFestivalCampaign';
import { DOUBLE_FESTIVAL_CAMPAIGN, SITE } from '@/app/constants';
import { calculateDaysUntilUTC, Q4_LAUNCH_CAMPAIGN } from '@/app/utils';
import { Q4LaunchBanner } from './components/Q4LaunchBanner';
import { RetentionBanner } from './components/RetentionBanner';
import { Vidu7DTrialBanner } from './components/Vidu7DTrialBanner';
import type { AnnouncementConfig } from './types';

/**
 * 顶部公告注册表
 *
 * 调度规则：
 * - 同一时间只展示一条公告，取 priority 最高且满足条件的
 * - priority 值越大优先级越高；新活动想覆盖旧活动，给更高 priority 即可
 * - expiresAt（可选）：UTC 过期时间，到期后自动隐藏；省略表示长驻
 * - condition（可选）：运行时条件函数，不满足时跳过，自动 fallback 到下一条
 * - 用户点击关闭后在当前位置永久隐藏（localStorage），工作台与官网分别记录，不影响其他公告
 *
 * 新增公告：在 components/ 下创建 Banner 组件，然后在此数组追加一条配置
 */
export const ANNOUNCEMENTS: AnnouncementConfig[] = [
  /**
   * @popupTitle Vidu Q4 上线顶部横幅
   * @popupSite both
   * @popupStart 海外 2026-10-07 21:00 / 国内 2026-10-08 09:00（北京时间）
   * @popupEnd 2026-10-13
   * @popupFrequency 用户关闭后在当前位置永久隐藏
   * @popupFigma https://www.figma.com/design/SIDn1gNJEGhPMgKzkfZueQ?node-id=41752-4574
   * @popupNote 点击切换 Q4 并进入参考生视频表单
   */
  {
    id: Q4_LAUNCH_CAMPAIGN.id,
    priority: 600,
    component: Q4LaunchBanner,
    condition: () => true,
  },
  /**
   * @popupTitle 双节礼遇顶部横幅
   * @popupSite both
   * @popupEnd 2026-10-01
   * @popupCondition 国内外站点 + 非企业分发 + 双节宣传开关开启 + 展示时间内；工作台与官网首页分别展示
   * @popupFrequency 工作台与官网分别记录关闭状态；用户关闭后仅当前位置本次活动永久隐藏（localStorage）
   * @popupNote 点击进入会员订阅页；官网仅展示本条活动；UTC 2026-10-01 16:00 自动下线
   */
  {
    id: DOUBLE_FESTIVAL_CAMPAIGN.id,
    priority: 500,
    component: DoubleFestivalBanner,
    condition: (ctx) => ctx.isDoubleFestivalActive,
  },
  /**
   * @popupTitle Vidu 7D 试用顶部横幅
   * @popupSite global
   * @popupFrequency 长驻；用户点击关闭后永久隐藏
   * @popupCondition 海外站 + 非团队空间 + 未订阅（subs_plan === 'free'）+ 具备首购优惠资格 + 非 t1 地区
   */
  {
    id: 'vidu_7d_trial_banner',
    priority: 400,
    component: (props) => <Vidu7DTrialBanner {...props} />,
    condition: (ctx) =>
      SITE === 'global' &&
      !ctx.isTeamSpace &&
      ctx.userInfo?.subs_plan === 'free' &&
      ctx.isSubOneTimeOffer &&
      ctx.countryLevel != null &&
      ctx.countryLevel !== 't1',
  },
  /**
   * @popupTitle 取消续费挽留横幅 - 最后一天
   * @popupSite china
   * @popupCondition 用户已取消续费 + 未处于提前续费冷却期 + 距到期 1 天内
   * @popupFrequency 长驻；用户点击关闭后永久隐藏
   * @popupNote 优先级最高，覆盖临期 7 天横幅
   */
  {
    id: 'retention_last_day',
    priority: 210,
    component: (props) => <RetentionBanner {...props} phase="lastDay" />,
    condition: (ctx) => {
      if (SITE !== 'china') return false;
      const sub = ctx.subscription;
      if (!sub?.auto_renew_canceled || sub.advance_renew_in_cooldown || !sub.end_time) return false;
      const days = calculateDaysUntilUTC(sub.end_time);
      return days > 0 && days <= 1;
    },
  },
  /**
   * @popupTitle 取消续费挽留横幅 - 临期 7 天
   * @popupSite china
   * @popupCondition 用户已取消续费 + 未处于提前续费冷却期 + 距到期 1~7 天
   * @popupFrequency 长驻；用户点击关闭后永久隐藏
   */
  {
    id: 'retention_expiring_7d',
    priority: 200,
    component: (props) => <RetentionBanner {...props} phase="expiring" />,
    condition: (ctx) => {
      if (SITE !== 'china') return false;
      const sub = ctx.subscription;
      if (!sub?.auto_renew_canceled || sub.advance_renew_in_cooldown || !sub.end_time) return false;
      const days = calculateDaysUntilUTC(sub.end_time);
      return days > 1 && days <= 7;
    },
  },
];
